import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Mail, 
  CheckCircle2,
  AlertCircle,
  Loader2,
  ArrowRight
} from "lucide-react";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const ConnectOutlookPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [subscriptionValid, setSubscriptionValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [connectedProvider, setConnectedProvider] = useState(null); // 'outlook' or 'gmail'

  // Check for OAuth callback params and subscription status
  useEffect(() => {
    const checkStatus = async () => {
      setCheckingStatus(true);
      
      // Check for OAuth success/error in URL
      const outlookSuccess = searchParams.get("outlook_success");
      const outlookError = searchParams.get("outlook_error");
      const gmailSuccess = searchParams.get("gmail_success");
      const gmailError = searchParams.get("gmail_error");
      const sessionId = searchParams.get("session_id");
      const email = searchParams.get("email");
      
      if (outlookSuccess === "true") {
        setIsConnected(true);
        setConnectedProvider("outlook");
        toast.success("Outlook connecté avec succès!", {
          description: email ? `Compte: ${email}` : undefined
        });
        // Redirect to dashboard after short delay
        setTimeout(() => navigate("/dashboard/inbox"), 1500);
        return;
      }
      
      if (gmailSuccess === "true") {
        setIsConnected(true);
        setConnectedProvider("gmail");
        toast.success("Gmail connecté avec succès!", {
          description: email ? `Compte: ${email}` : undefined
        });
        setTimeout(() => navigate("/dashboard/inbox"), 1500);
        return;
      }
      
      if (outlookError) {
        const errorMessages = {
          missing_code: "Code d'autorisation manquant",
          not_configured: "Microsoft Outlook non configuré",
          token_failed: "Échec de l'authentification",
          no_token: "Token non reçu",
          callback_failed: "Erreur de callback"
        };
        setErrorMessage(errorMessages[outlookError] || `Erreur Outlook: ${outlookError}`);
      }
      
      if (gmailError) {
        const errorMessages = {
          missing_code: "Code d'autorisation manquant",
          not_configured: "Gmail non configuré",
          callback_failed: "Erreur de callback"
        };
        setErrorMessage(errorMessages[gmailError] || `Erreur Gmail: ${gmailError}`);
      }
      
      // If we have a session_id from Stripe checkout, verify and activate subscription first
      if (sessionId) {
        try {
          const verifyResponse = await fetch(`${API}/billing/verify-session/${sessionId}`, {
            credentials: "include"
          });
          
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            if (verifyData.status === "success") {
              toast.success("Paiement confirmé!", {
                description: `Plan ${verifyData.plan_id} activé avec succès`
              });
              setSubscriptionValid(true);
            }
          }
        } catch (error) {
          console.error("Error verifying session:", error);
        }
      }
      
      // Check subscription and connection status
      try {
        const response = await fetch(`${API}/billing/subscription-status`, {
          credentials: "include"
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscriptionValid(data.subscription_status === "trialing" || data.subscription_status === "active");
          setIsConnected(data.outlook_connected);
          
          // If already connected, redirect to dashboard
          if (data.outlook_connected && data.can_access_dashboard) {
            navigate("/dashboard/inbox");
            return;
          }
          
          // If no valid subscription, redirect to pricing
          if (data.subscription_status !== "trialing" && data.subscription_status !== "active") {
            toast.error("Abonnement requis");
            navigate("/pricing");
            return;
          }
        }
      } catch (error) {
        console.error("Status check error:", error);
      }
      
      setCheckingStatus(false);
    };
    
    checkStatus();
  }, [searchParams, navigate]);

  const handleConnectOutlook = async () => {
    setLoading(true);
    setErrorMessage(null);
    
    try {
      const response = await fetch(`${API}/auth/microsoft/login?language=fr`, {
        credentials: "include"
      });
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 503) {
          const msg = data.detail?.message_en || data.detail?.message_fr || "Microsoft not configured";
          setErrorMessage(msg);
          toast.error(msg);
        } else {
          setErrorMessage("Erreur lors de la connexion Microsoft");
          toast.error("Erreur lors de la connexion Microsoft");
        }
        setLoading(false);
        return;
      }
      
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        setErrorMessage("URL de connexion Microsoft non disponible");
        toast.error("URL de connexion Microsoft non disponible");
        setLoading(false);
      }
    } catch (error) {
      console.error("Microsoft login error:", error);
      setErrorMessage("Erreur de connexion au serveur");
      toast.error("Erreur de connexion au serveur");
      setLoading(false);
    }
  };

  const handleConnectGmail = async () => {
    setGmailLoading(true);
    setErrorMessage(null);
    
    try {
      const response = await fetch(`${API}/auth/gmail/login?language=fr`, {
        credentials: "include"
      });
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 503) {
          const msg = data.detail?.message_en || data.detail?.message_fr || "Gmail not configured";
          setErrorMessage(msg);
          toast.error(msg);
        } else {
          setErrorMessage("Erreur lors de la connexion Gmail");
          toast.error("Erreur lors de la connexion Gmail");
        }
        setGmailLoading(false);
        return;
      }
      
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        setErrorMessage("URL de connexion Gmail non disponible");
        toast.error("URL de connexion Gmail non disponible");
        setGmailLoading(false);
      }
    } catch (error) {
      console.error("Gmail login error:", error);
      setErrorMessage("Erreur de connexion au serveur");
      toast.error("Erreur de connexion au serveur");
      setGmailLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Vérification...</p>
        </div>
      </div>
    );
  }

  if (isConnected) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#020617] flex items-center justify-center p-4">
        <Card className="glass-card max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              {connectedProvider === "gmail" ? "Gmail connecté!" : "Outlook connecté!"}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              Votre compte est prêt. Redirection vers le tableau de bord...
            </p>
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617] flex items-center justify-center p-4">
      <Card className="glass-card max-w-lg w-full" data-testid="connect-outlook-card">
        <CardContent className="p-8 md:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src="/logo-alia.png" style={{height: "80px", objectFit: "contain"}} alt="ALIA Assistant" />
            </div>
            <Badge className="mb-4 bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20">
              Essai activé ✓
            </Badge>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Outfit' }}>
              Connectez votre Outlook
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Dernière étape : connectez votre compte Microsoft Outlook pour activer ALIA
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-6">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">{errorMessage}</p>
            </div>
          )}

          {/* Connect Button */}
          <Button 
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg"
            onClick={handleConnectOutlook}
            disabled={loading}
            data-testid="connect-outlook-btn"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Connexion en cours...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6" viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Connecter Microsoft Outlook
                <ArrowRight className="w-5 h-5 ml-1" />
              </div>
            )}
          </Button>

          {/* Info */}
          <div className="mt-6 p-4 rounded-xl bg-slate-100 dark:bg-slate-800/50">
            <h4 className="font-medium text-slate-900 dark:text-white mb-2 text-sm">
              Pourquoi connecter Outlook ?
            </h4>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              <li>• ALIA analyse vos emails en temps réel</li>
              <li>• Catégorisation automatique par priorité</li>
              <li>• Suggestions de réponses intelligentes</li>
              <li>• Vos données restent privées et sécurisées</li>
            </ul>
          </div>

          {/* Security Note */}
          <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-6">
            ALIA utilise l'authentification officielle Microsoft OAuth 2.0. 
            Nous ne stockons jamais vos identifiants.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConnectOutlookPage;
